import React, { useState, useRef } from "react";
import { X, User, Users, Hash, Mail, Phone, BookOpen, ShieldCheck, MapPin, Loader2, Edit2, Camera } from "lucide-react";
import { supabase } from "../../lib/supabase"; // Ensure this is correctly imported
import toast from "react-hot-toast";

export default function EditModal({ student, isUpdating, onClose, onSave }) {
  const [formData, setFormData] = useState({ ...student });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const inputStyles = "w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-orange-500 transition-all";
  const labelStyles = "text-[10px] font-black uppercase text-slate-400 ml-1 mb-1 block";
  const iconStyles = "absolute left-3 top-1/2 -translate-y-1/2 text-slate-400";

  // Handle Photo Selection & Upload
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${student.id}-${Math.random()}.${fileExt}`;
      const filePath = `photos/${fileName}`;

      // 1. Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('DivyaInstitite') // Ensure this bucket exists in your Supabase dashboard
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 2. Get Public URL
      const { data } = supabase.storage
        .from('DivyaInstitite')
        .getPublicUrl(filePath);

      setFormData({ ...formData, photo_url: data.publicUrl });
      toast.success("Photo uploaded successfully");
    } catch (error) {
      toast.error("Error uploading photo");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Remove generated/read-only columns before update
    const {
      pending_balance,
      settled_amount,
      created_at,
      updated_at,
      ...cleanData
    } = formData;

    // Convert empty email to null
    cleanData.email = cleanData.email?.trim() || null;

    // Send only editable fields
    onSave(cleanData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="p-5 border-b bg-slate-900 text-white flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Edit2 size={18} className="text-orange-500" />
            <h3 className="font-black italic tracking-tight">Edit Student Profile</h3>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Photo Upload Section */}
          <div className="flex flex-col items-center justify-center space-y-3 pb-4">
            <div className="relative group cursor-pointer" onClick={() => fileInputRef.current.click()}>
              <div className="w-24 h-24 rounded-full border-4 border-orange-50 overflow-hidden bg-slate-100 flex items-center justify-center">
                {formData.photo_url ? (
                  <img src={formData.photo_url} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <User size={40} className="text-slate-300" />
                )}
              </div>
              <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploading ? <Loader2 className="text-white animate-spin" size={20} /> : <Camera className="text-white" size={20} />}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoChange}
                className="hidden"
                accept="image/*"
              />
            </div>
            <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Click to update photo</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelStyles}>Full Name</label>
              <div className="relative">
                <User className={iconStyles} size={14} />
                <input type="text" value={formData.full_name || ""}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className={inputStyles} />
              </div>
            </div>

            <div>
              <label className={labelStyles}>Parent Name</label>
              <div className="relative">
                <Users className={iconStyles} size={14} />
                <input type="text" value={formData.parent_name || ""}
                  onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })} className={inputStyles} />
              </div>
            </div>

            <div>
              <label className={labelStyles}>Roll Number</label>
              <div className="relative">
                <Hash className={iconStyles} size={14} />
                <input type="text" value={formData.roll_no || ""}
                  onChange={(e) => setFormData({ ...formData, roll_no: e.target.value })} className={inputStyles} />
              </div>
            </div>

            <div>
              <label className={labelStyles}>Gender</label>
              <div className="relative">
                <ShieldCheck className={iconStyles} size={14} />
                <select
                  value={formData.gender || "Male"}
                  onChange={(e) =>
                    setFormData({ ...formData, gender: e.target.value })
                  }
                  className={`${inputStyles} appearance-none`}
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelStyles}>Date of Birth</label>
              <div className="relative">
                <Hash className={iconStyles} size={14} />
                <input
                  type="date"
                  value={formData.dob || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, dob: e.target.value })
                  }
                  className={inputStyles}
                />
              </div>
            </div>

            <div>
              <label className={labelStyles}>Admission Date</label>
              <div className="relative">
                <Hash className={iconStyles} size={14} />
                <input
                  type="date"
                  value={formData.admission_date || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      admission_date: e.target.value,
                    })
                  }
                  className={inputStyles}
                />
              </div>
            </div>

            <div>
              <label className={labelStyles}>Phone Number</label>
              <div className="relative">
                <Phone className={iconStyles} size={14} />
                <input type="tel" value={formData.phone || ""}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputStyles} />
              </div>
            </div>
  <div>
              <label className={labelStyles}>Email</label>
              <div className="relative">
                <Phone className={iconStyles} size={14} />
                <input type="email" value={formData.email || ""}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputStyles} />
              </div>
            </div>

            <div>
              <label className={labelStyles}>Address</label>
              <div className="relative">
                <MapPin className={iconStyles} size={14} />
                <input type="text" value={formData.address || ""}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })} className={inputStyles} />
              </div>
            </div>

            <div>
              <label className={labelStyles}>Education</label>
              <div className="relative">
                <BookOpen className={iconStyles} size={14} />
                <input
                  type="text"
                  value={formData.education || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      education: e.target.value,
                    })
                  }
                  className={inputStyles}
                  placeholder="e.g. 12th / BCA"
                />
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-white">
            <button
              type="submit"
              disabled={isUpdating || uploading}
              className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-bold text-xs hover:bg-orange-600 flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {(isUpdating || uploading) ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
              Update Record
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-xs hover:bg-slate-200 transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}